import { useState, useEffect, useRef } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { apiGet } from '../../lib/apiClient'

interface Testimonial {
  _id: string
  studentName: string
  courseName: string
  feedback: string
  rating: number
  photo?: string
}

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [current, setCurrent] = useState(0)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiGet('/testimonials?approved=true', { skipAuth: true })
        if (response.ok) {
          const data = await response.json()
          setTestimonials(data.testimonials || [])
        }
      } catch (error) {
        console.error('Error:', error)
      }
    }
    fetchTestimonials()
  }, [])

  useEffect(() => {
    if (testimonials.length === 0) return
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [testimonials.length])

  if (testimonials.length === 0) return null

  return (
    <section id="testimonials" ref={ref} className="relative bg-white dark:bg-neutral-900 py-32 overflow-hidden transition-colors duration-500">
      {/* Giant Quote Mark Background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[50vw] font-serif text-charcoal-50 dark:text-white/5 select-none pointer-events-none leading-none transition-colors duration-500">
        "
      </div>

      <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          className="mb-20"
        >
          <span className="text-rose-500 font-mono text-xs uppercase tracking-[0.3em] block mb-6">
            Voices
          </span>
          <h2 className="text-5xl md:text-7xl font-serif font-black text-charcoal-900 dark:text-white leading-[0.9]">
            What They<br />Say
          </h2>
        </motion.div>

        {/* Testimonial Display */}
        <div className="max-w-4xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ duration: 0.6 }}
            >
              {/* Quote */}
              <blockquote className="text-3xl md:text-5xl font-serif text-charcoal-800 dark:text-neutral-200 leading-[1.3] mb-12">
                "{testimonials[current].feedback}"
              </blockquote>

              {/* Attribution */}
              <div className="flex items-center gap-8">
                {testimonials[current].photo ? (
                  <img
                    src={testimonials[current].photo}
                    alt={testimonials[current].studentName}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-charcoal-100 dark:bg-white/10 flex items-center justify-center text-charcoal-900 dark:text-white text-xl font-bold">
                    {testimonials[current].studentName.charAt(0)}
                  </div>
                )}
                <div>
                  <div className="text-charcoal-900 dark:text-white text-lg font-medium">{testimonials[current].studentName}</div>
                  <div className="text-charcoal-500 dark:text-neutral-400 text-sm">{testimonials[current].courseName}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-4 mt-16">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className={`h-1 transition-all duration-300 ${index === current ? 'w-12 bg-rose-500' : 'w-4 bg-charcoal-200 dark:bg-white/20 hover:bg-charcoal-400 dark:hover:bg-white/40'
                }`}
            />
          ))}
        </div>
      </div>
    </section>
  )
}